import { WarningOutlined } from '@ant-design/icons'
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  RowData,
  useReactTable,
} from '@tanstack/react-table'
import { useWindowVirtualizer } from '@tanstack/react-virtual'
import { Checkbox, Tooltip } from 'antd'
import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

import { useUserInfoStore } from '@/stores/user/userInfoStore'

import cn from './VirtualizedCartTable.module.scss'

interface RowSelection<T extends RowData> {
  selectedRowKeys?: (string | number | bigint)[]
  onChange: (keys: (string | number | bigint)[], rows: T[]) => void
  canSelectAll?: (record: T) => boolean
}

interface VirtualizedCartTableProps<T extends RowData> {
  data: T[]
  columns: ColumnDef<T, unknown>[]
  rowKey: (record: T) => string | number
  onRow?: (record: T) => { id?: string; onClick?: () => void }
  rowSelection?: RowSelection<T>
  rowClassName?: (record: T) => string
  highlightedProductId?: string | null
  getCheckboxProps?: (record: T) => { disabled?: boolean }
  getRowTooltip?: (record: T) => string | null
}

const VirtualizedCartTable = <T extends RowData>({
  data,
  columns,
  rowKey,
  onRow,
  rowSelection,
  rowClassName,
  highlightedProductId,
  getRowTooltip,
}: VirtualizedCartTableProps<T>) => {
  const table = useReactTable<T>({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
  })

  const { selectedOrganisation } = useUserInfoStore((state) => state)

  const parentRef = useRef<HTMLDivElement | null>(null)
  const innerRef = useRef<HTMLDivElement | null>(null)
  const originalHeaderRef = useRef<HTMLDivElement | null>(null)
  const floatingHeaderRef = useRef<HTMLDivElement | null>(null)
  const [contentWidth, setContentWidth] = useState<number>(0)
  const isFloatingVisibleRef = useRef(false)

  useEffect(() => {
    if (!innerRef.current) return

    const updateWidth = () => {
      if (innerRef.current) {
        setContentWidth(innerRef.current.scrollWidth)
      }
    }

    updateWidth()

    const resizeObserver = new ResizeObserver(() => {
      updateWidth()
    })

    if (innerRef.current) {
      resizeObserver.observe(innerRef.current)
    }

    return () => {
      resizeObserver.disconnect()
    }
  }, [data, columns])

  useEffect(() => {
    const el = originalHeaderRef.current
    if (!el) return

    const navHeader = document.querySelector('header')
    const searchBar = document.querySelector(
      '[class*="titleWithOrganisationsSearch"]'
    )

    const getStickyTop = () => {
      const headerHeight = navHeader?.offsetHeight ?? 0
      if (searchBar instanceof HTMLElement) {
        return headerHeight + searchBar.offsetHeight
      }
      return headerHeight
    }

    const updatePosition = () => {
      const el = floatingHeaderRef.current
      if (!el) return
      const top = getStickyTop()
      const tableRect = parentRef.current?.getBoundingClientRect()
      el.style.top = `${top}px`
      el.style.left = `${tableRect?.left ?? 0}px`
      el.style.width = `${tableRect?.width ?? 0}px`
    }
    updatePosition()

    const setVisible = (visible: boolean) => {
      const floatingEl = floatingHeaderRef.current
      if (!floatingEl) return
      if (visible === isFloatingVisibleRef.current) return
      isFloatingVisibleRef.current = visible
      if (visible) {
        floatingEl.classList.remove(cn.floatingHeaderHide)
        floatingEl.classList.add(cn.floatingHeaderVisible)
      } else {
        floatingEl.classList.remove(cn.floatingHeaderVisible)
        floatingEl.classList.add(cn.floatingHeaderHide)
      }
    }

    const createObserver = () => {
      const offset = getStickyTop()
      return new IntersectionObserver(
        ([entry]) => setVisible(!entry.isIntersecting),
        { threshold: 0, rootMargin: `-${offset}px 0px 0px 0px` }
      )
    }

    let observer = createObserver()
    observer.observe(el)

    const navResizeObserver = new ResizeObserver(() => {
      updatePosition()
      observer.disconnect()
      observer = createObserver()
      observer.observe(el)
    })
    if (navHeader) navResizeObserver.observe(navHeader)
    if (searchBar) navResizeObserver.observe(searchBar)

    // left/width обновляем при resize таблицы
    const tableResizeObserver = new ResizeObserver(() => {
      const el = floatingHeaderRef.current
      if (!el) return
      const tableRect = parentRef.current?.getBoundingClientRect()
      el.style.left = `${tableRect?.left ?? 0}px`
      el.style.width = `${tableRect?.width ?? 0}px`
    })
    if (parentRef.current) tableResizeObserver.observe(parentRef.current)

    // При горизонтальном scroll контейнера обновляем left плавающей шапки
    const onScroll = () => {
      const floatingEl = floatingHeaderRef.current
      if (!floatingEl) return
      const tableRect = parentRef.current?.getBoundingClientRect()
      floatingEl.style.left = `${tableRect?.left ?? 0}px`
    }
    const container = parentRef.current
    container?.addEventListener('scroll', onScroll, { passive: true })

    return () => {
      observer.disconnect()
      navResizeObserver.disconnect()
      tableResizeObserver.disconnect()
      container?.removeEventListener('scroll', onScroll)
    }
  }, [])

  const rowVirtualizer = useWindowVirtualizer({
    count: table.getRowModel().rows.length,
    estimateSize: (index) => {
      const row = table.getRowModel().rows[index]
      const text =
        (row.original as { nomenclatureDto?: { description?: string } })
          ?.nomenclatureDto?.description ?? ''

      const charactersPerLine = 40
      const lines = Math.ceil(text.length / charactersPerLine) || 1
      const lineHeight = 22
      const basePadding = 32

      return Math.max(70, lines * lineHeight + basePadding)
    },
    overscan: 10,
  })

  const virtualItems = rowVirtualizer.getVirtualItems()
  const totalSize = rowVirtualizer.getTotalSize()

  const toggleRowSelection = (record: T) => {
    if (!rowSelection) return

    const key = rowKey(record)
    const selected = new Set(rowSelection.selectedRowKeys ?? [])
    if (selected.has(key)) selected.delete(key)
    else selected.add(key)

    const newKeys = Array.from(selected)
    const selectedRows = data.filter((r) => newKeys.includes(rowKey(r)))
    rowSelection.onChange(newKeys, selectedRows)
  }

  const getEnabledData = () => {
    if (!rowSelection?.canSelectAll) return data
    return data.filter((record) => rowSelection.canSelectAll?.(record))
  }

  const enabledData = getEnabledData()
  const enabledDataCount = enabledData.length
  const selectedKeys = new Set(rowSelection?.selectedRowKeys ?? [])
  const selectedCount = selectedKeys.size

  const isAllSelected =
    enabledDataCount > 0 &&
    enabledData.every((record) => selectedKeys.has(rowKey(record)))
  const isIndeterminate = selectedCount > 0 && !isAllSelected

  return (
    <div ref={parentRef} className={cn.virtualTableContainer}>
      <div
        ref={innerRef}
        className={cn.virtualTableInner}
        style={{
          '--row-width': contentWidth > 0 ? `${contentWidth}px` : '100%',
        }}
      >
        {/* HEADER */}
        <div
          ref={originalHeaderRef}
          className={
            Object.keys(selectedOrganisation).length
              ? [
                  cn.virtualTableHeader,
                  cn.organisationSelected,
                  cn.gridTemplate,
                ].join(' ')
              : [cn.virtualTableHeader, cn.gridTemplate].join(' ')
          }
        >
          {rowSelection && (
            <div
              className={
                Object.keys(selectedOrganisation).length
                  ? [cn.largeCheckbox, cn.organisationSelected].join(' ')
                  : cn.largeCheckbox
              }
            >
              <Checkbox
                id="select-all-checkbox"
                name="select-all-checkbox"
                checked={isAllSelected}
                indeterminate={isIndeterminate}
                disabled={enabledDataCount === 0}
                onChange={() => {
                  if (isAllSelected) {
                    rowSelection.onChange([], [])
                  } else {
                    const validData = getEnabledData()
                    rowSelection.onChange(
                      validData.map((r) => rowKey(r)),
                      validData
                    )
                  }
                }}
              />
            </div>
          )}
          {table.getHeaderGroups().map((headerGroup) =>
            headerGroup.headers.map((header) => (
              <div key={header.id} className={cn.virtualCell}>
                {flexRender(
                  header.column.columnDef.header,
                  header.getContext()
                )}
              </div>
            ))
          )}
        </div>

        {createPortal(
          <div
            ref={floatingHeaderRef}
            className={[
              cn.floatingHeader,
              cn.gridTemplate,
              Object.keys(selectedOrganisation).length
                ? cn.organisationSelected
                : '',
            ].join(' ')}
          >
            {rowSelection && (
              <div
                className={
                  Object.keys(selectedOrganisation).length
                    ? [cn.largeCheckbox, cn.organisationSelected].join(' ')
                    : cn.largeCheckbox
                }
              >
                <Checkbox
                  checked={isAllSelected}
                  indeterminate={isIndeterminate}
                  disabled={enabledDataCount === 0}
                  onChange={() => {
                    if (isAllSelected) {
                      rowSelection.onChange([], [])
                    } else {
                      const validData = getEnabledData()
                      rowSelection.onChange(
                        validData.map((r) => rowKey(r)),
                        validData
                      )
                    }
                  }}
                />
              </div>
            )}
            {table.getHeaderGroups().map((headerGroup) =>
              headerGroup.headers.map((header) => (
                <div key={header.id} className={cn.virtualCell}>
                  {flexRender(
                    header.column.columnDef.header,
                    header.getContext()
                  )}
                </div>
              ))
            )}
          </div>,
          document.body
        )}

        {/* BODY */}
        <div
          className={cn.virtualList}
          style={{ '--virtual-total-size': `${totalSize}px` }}
        >
          {virtualItems.map((virtualRow) => {
            const row = table.getRowModel().rows[virtualRow.index]
            const record = row.original
            const key = String(rowKey(record))
            const rowMeta = onRow ? onRow(record) : {}
            const isHighlighted = highlightedProductId === key
            const customClass = rowClassName ? rowClassName(record) : ''

            const isChecked =
              rowSelection?.selectedRowKeys?.includes(key) ?? false
            const rowTooltip = getRowTooltip ? getRowTooltip(record) : null

            return (
              <div
                key={key}
                id={rowMeta?.id || `row-${key}`}
                className={[
                  cn.virtualRow,
                  cn.virtualRowPositioned,
                  cn.gridTemplate,
                  customClass,
                  isHighlighted ? cn.highlightRow : '',
                  Object.keys(selectedOrganisation).length
                    ? cn.organisationSelected
                    : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
                style={{
                  '--virtual-row-size': `${virtualRow.size}px`,
                  '--virtual-row-start': `${virtualRow.start}px`,
                }}
                onClick={() => rowMeta?.onClick?.()}
              >
                {/* Checkbox column */}
                {rowSelection && (
                  <div
                    className={
                      Object.keys(selectedOrganisation).length
                        ? [cn.largeCheckbox, cn.organisationSelected].join(' ')
                        : cn.largeCheckbox
                    }
                  >
                    <Checkbox
                      id={`checkbox-${key}`}
                      name={`checkbox-${key}`}
                      checked={isChecked}
                      onChange={() => toggleRowSelection(record)}
                      onClick={(e) => e.stopPropagation()}
                    />
                    {rowTooltip && (
                      <Tooltip title={rowTooltip} placement="right">
                        <WarningOutlined className={cn.rowWarningIcon} />
                      </Tooltip>
                    )}
                  </div>
                )}

                {/* Data cells */}
                {row.getVisibleCells().map((cell) => (
                  <div key={cell.id} className={cn.virtualCell}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </div>
                ))}
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

export default VirtualizedCartTable
